import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Upload, File, X, CheckCircle, AlertTriangle, Trash2, Shield, ShieldAlert } from 'lucide-react';
import { arcSecurityHandler } from '../../utils/arcSecurity';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  status: 'validating' | 'scanning' | 'uploading' | 'completed' | 'error' | 'infected';
  progress: number;
  url?: string;
  scanResult?: {
    threats: Record<string, unknown>[];
    scanId: string;
  };
  validationError?: string;
}

interface FileUploadProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  taskId?: string;
  onUploadComplete?: (files: File[]) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ isOpen, onClose, projectId, taskId, onUploadComplete }) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = useCallback(async (fileList: File[]) => {
    setError(null);
    
    const newFiles: UploadedFile[] = fileList.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'validating',
      progress: 0
    }));

    setFiles(prev => [...prev, ...newFiles]);

    for (const uploadFile of newFiles) {
      const originalFile = fileList.find(f => f.name === uploadFile.name)!;
      await processFileWithSecurity(uploadFile, originalFile);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, [handleFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const processFileWithSecurity = async (uploadFile: UploadedFile, file: File) => {
    try {
      setFiles(prev => prev.map(f => 
        f.id === uploadFile.id ? { ...f, status: 'validating' } : f
      ));

      const validation = await arcSecurityHandler.validate({
        files: [{
          name: file.name,
          size: file.size,
          type: file.type,
          extension: getFileExtension(file.name)
        }]
      });

      const fileValidation = (validation as { validationResults: Array<{ valid: boolean; reason?: string }> }).validationResults[0];
      if (!fileValidation.valid) {
        setFiles(prev => prev.map(f => 
          f.id === uploadFile.id 
            ? { ...f, status: 'error', validationError: fileValidation.reason || 'Validation failed' }
            : f
        ));
        return;
      }

      setFiles(prev => prev.map(f => 
        f.id === uploadFile.id ? { ...f, status: 'scanning' } : f
      ));

      const tempPath = await createTempFile();
      
      const scanResult = await arcSecurityHandler.scan({
        filePath: tempPath,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        sessionId: `upload_${uploadFile.id}`
      });

      if ((scanResult as { status: string; threats?: Record<string, unknown>[]; scanId?: string }).status === 'infected') {
        setFiles(prev => prev.map(f => 
          f.id === uploadFile.id 
            ? { 
                ...f, 
                status: 'infected',
                scanResult: {
                  threats: (scanResult as { threats?: Record<string, unknown>[] }).threats || [],
                  scanId: (scanResult as { scanId?: string }).scanId || ''
                }
              }
            : f
        ));
        await cleanupTempFile(tempPath);
        return;
      }

      if ((scanResult as { status: string }).status === 'error') {
        setFiles(prev => prev.map(f => 
          f.id === uploadFile.id 
            ? { ...f, status: 'error', validationError: 'Security scan failed' }
            : f
        ));
        await cleanupTempFile(tempPath);
        return;
      }

      await uploadFileToServer(uploadFile, file);
      await cleanupTempFile(tempPath);

    } catch (error) {
      console.error('Security processing failed:', error);
      setFiles(prev => prev.map(f => 
        f.id === uploadFile.id 
          ? { ...f, status: 'error', validationError: 'Security processing failed' }
          : f
      ));
    }
  };

  const createTempFile = async (): Promise<string> => {
    const tempPath = `/tmp/upload_${Date.now()}_${Math.random().toString(36).substring(2)}`;
    
    return tempPath;
  };

  const cleanupTempFile = async (tempPath: string): Promise<void> => {
    console.log(`[FileUpload] Cleaning up temp file: ${tempPath}`);
  };

  const getFileExtension = (filename: string): string => {
    const lastDot = filename.lastIndexOf('.');
    return lastDot === -1 ? '' : filename.substring(lastDot).toLowerCase();
  };

  const uploadFileToServer = async (uploadFile: UploadedFile, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);

    try {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setFiles(prev => prev.map(f => 
            f.id === uploadFile.id ? { ...f, progress } : f
          ));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          setFiles(prev => prev.map(f => 
            f.id === uploadFile.id 
              ? { 
                  ...f, 
                  status: 'completed', 
                  progress: 100, 
                  url: `/api/storage/${encodeURIComponent(response.fileId)}` 
                }
              : f
          ));
        } else {
          setFiles(prev => prev.map(f => 
            f.id === uploadFile.id 
              ? { ...f, status: 'error', progress: 0 }
              : f
          ));
        }
      });

      xhr.addEventListener('error', () => {
        setFiles(prev => prev.map(f => 
          f.id === uploadFile.id 
            ? { ...f, status: 'error', progress: 0 }
            : f
        ));
      });

      xhr.open('POST', '/api/storage/upload');
      xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('authToken')}`);
      xhr.send(formData);

    } catch {
      setFiles(prev => prev.map(f => 
        f.id === uploadFile.id 
          ? { ...f, status: 'error', progress: 0 }
          : f
      ));
      setError('Upload failed');
    }
  };

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const clearAll = () => {
    setFiles([]);
    setError(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('text/')) return '📄';
    if (type.includes('pdf')) return '📕';
    if (type.includes('zip') || type.includes('tar')) return '📦';
    return '📄';
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-slate-900 border border-slate-700 rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-white">File Upload</h2>
          <div className="flex items-center space-x-2">
            {files.length > 0 && (
              <motion.button
                onClick={clearAll}
                className="flex items-center space-x-2 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Trash2 size={16} />
                <span>Clear All</span>
              </motion.button>
            )}
            <motion.button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={20} />
            </motion.button>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 mb-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="text-red-400" size={16} />
              <span className="text-red-400">{error}</span>
            </div>
          </div>
        )}

        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive 
              ? 'border-blue-500 bg-blue-500/10' 
              : 'border-slate-600 hover:border-slate-500'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="mx-auto mb-4 text-slate-400" size={48} />
          <h3 className="text-lg font-medium text-white mb-2">
            Drop files here or click to browse
          </h3>
          <p className="text-slate-400 mb-4">
            Support for images, documents, code files, and archives
          </p>
          <input
            type="file"
            multiple
            onChange={handleFileInput}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors cursor-pointer"
          >
            <File size={16} />
            <span>Choose Files</span>
          </label>
        </div>

        {files.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-white mb-4">
              Uploaded Files ({files.length})
            </h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {files.map((file) => (
                <motion.div
                  key={file.id}
                  className="bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 border border-slate-700 rounded-lg p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <span className="text-2xl">{getFileIcon(file.type)}</span>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-white font-medium truncate">{file.name}</span>
                          {file.status === 'completed' && (
                            <CheckCircle className="text-green-400" size={16} />
                          )}
                          {file.status === 'error' && (
                            <AlertTriangle className="text-red-400" size={16} />
                          )}
                          {file.status === 'infected' && (
                            <ShieldAlert className="text-red-500" size={16} />
                          )}
                          {(file.status === 'validating' || file.status === 'scanning') && (
                            <Shield className="text-yellow-400 animate-pulse" size={16} />
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-slate-400">
                          <span>{formatFileSize(file.size)}</span>
                          <span>{file.type || 'Unknown type'}</span>
                          {file.status === 'uploading' && (
                            <span>{file.progress}%</span>
                          )}
                          {file.status === 'validating' && (
                            <span className="text-yellow-400">Validating...</span>
                          )}
                          {file.status === 'scanning' && (
                            <span className="text-yellow-400">Scanning for malware...</span>
                          )}
                          {file.status === 'infected' && file.scanResult && (
                            <span className="text-red-400">
                              Infected: {file.scanResult.threats.join(', ')}
                            </span>
                          )}
                          {file.status === 'error' && file.validationError && (
                            <span className="text-red-400">{file.validationError}</span>
                          )}
                        </div>
                        {file.status === 'uploading' && (
                          <div className="mt-2 w-full bg-slate-700 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${file.progress}%` }}
                            ></div>
                          </div>
                        )}
                        {(file.status === 'validating' || file.status === 'scanning') && (
                          <div className="mt-2 w-full bg-slate-700 rounded-full h-2">
                            <div className="bg-yellow-500 h-2 rounded-full animate-pulse w-full"></div>
                          </div>
                        )}
                        {file.status === 'infected' && (
                          <div className="mt-2 p-2 bg-red-900/20 border border-red-500/30 rounded text-xs">
                            <div className="flex items-center space-x-2 text-red-400">
                              <ShieldAlert size={14} />
                              <span className="font-medium">Security Threat Detected</span>
                            </div>
                            {file.scanResult && (
                              <div className="mt-1 text-red-300">
                                <div>Threats: {file.scanResult.threats.join(', ')}</div>
                                <div>Scan ID: {file.scanResult.scanId}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <motion.button
                      onClick={() => removeFile(file.id)}
                      className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <X size={16} />
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default FileUpload;
